/**
 * The API failure type shared by `getJSON` / `postJSON`, the global react-query
 * error handler and Sentry's `beforeSend`. Kept free of imports so any of them
 * can pull it in without dragging the store or the UI kit along.
 */

/** Copy shown when the API gives us nothing worth putting in front of a user. */
export const GENERIC_API_ERROR_MESSAGE =
  'Oops! Something went wrong. Please refresh the page and try again.'

export class ApiError extends Error {
  readonly status: number
  /** Parsed response body, exactly as the API sent it. */
  readonly responseBody: unknown

  constructor(status: number, responseBody: unknown, message?: string) {
    super(message || `Request failed with status ${status}`)

    // Callers all over the app read `err.code` / `err.msg` / `err.detail` off
    // the rejected value, which used to be the response body itself. Copying
    // the body's keys across keeps them working; our own fields are assigned
    // afterwards so a body carrying `status`/`message`/`stack` can't shadow
    // them.
    const ownMessage = this.message
    const ownStack = this.stack
    if (responseBody && typeof responseBody === 'object') {
      Object.assign(this, responseBody)
    }
    this.name = 'ApiError'
    this.message = ownMessage
    this.stack = ownStack
    this.status = status
    this.responseBody = responseBody
  }
}

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError

/**
 * First usable message the API put in the body. Endpoints use `message`, `msg`
 * or `error` depending on their age, so callers that need a specific
 * precedence (see `apiErrorMessage`) pass their own `keys`.
 */
export const serverErrorMessage = (
  body: unknown,
  keys: readonly string[] = ['message', 'msg', 'error']
): string | undefined => {
  if (typeof body === 'string') return body || undefined
  if (!body || typeof body !== 'object') return undefined

  const fields = body as Record<string, unknown>
  for (const key of keys) {
    const value = fields[key]
    if (typeof value === 'string' && value) return value
  }
  return undefined
}

/**
 * `lmcRequest` shares one AbortController, so starting a request with
 * `newSignal` cancels whatever else is in flight. Those rejections are
 * bookkeeping, not failures.
 */
export const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError'
